// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import ConfirmationScreenTemplate from '@front/templates/ConfirmationScreenTemplate.html';
import { confirmationScreenHandler } from '@shared/utils/AppHandlers';
import { ScreensNames } from '@front/controls/UtilsClasses/FactoryConstants';
import { MainWindowManager } from '@front/controls/UtilsClasses/MainWindowManager';
import { MessageBoxType } from '@shared/utils/Interfaces/Screens/MessageBox.types';
import { TerceraMessageBox } from '@front/controls/screen/TerceraMessageBox';
import { TerceraWindowBase } from '@front/controls/screen/TerceraWindowBase';
import { KeyEventProcessor } from '../KeyEventProcessor';
import { Resources } from '@shared/localizations/Resources';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { OrderFormatter } from '../cache/Formatters/OrderFormatter';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { DataCacheHolder } from '../cache/DataCacheHolder';
import { OrderEditRequestData } from '@shared/utils/Trading/OrderEditRequestData';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { MarginInfoUtils } from '../UtilsClasses/MarginInfo/MarginInfoUtils';
import { BaseSettings } from '../Settings/BaseGeneralSettingsWrapper';
export class ConfirmationScreen extends TerceraWindowBase {
    static get DataCache() {
        return DataCacheHolder.getDataCache();
    }
    constructor() {
        super();
    }
    getType() {
        return ScreensNames.ConfirmationScreen;
    }
    getClientPanel() {
        return this.find('.confirmation-screen-container');
    }
    oninit() {
        super.oninit();
    }
    oncomplete() {
        super.oncomplete();
        void this.initializeConfirmation();
        TerceraMessageBox.PlaySound(this.params.type);
    }
    initializeConfirmation() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateWithRequestData(this.params.reqData);
            this.updateMsgBox();
            this.setFocus();
            this.center();
            const asyncConfirmationParams = this.params.asyncConfirmationParams;
            if (asyncConfirmationParams == null) {
                return;
            }
            const result = yield asyncConfirmationParams;
            if (!result) {
                return;
            }
            this.params.reqData = result.reqData;
            if (result.warningText !== undefined) {
                this.params.errorText = result.warningText;
            }
            yield this.updateWithRequestData(result.reqData);
            this.updateMsgBox();
        });
    }
    dispose() {
        super.dispose();
    }
    onCloseButtonClick() {
        if (!isNullOrUndefined(this.params.cancelCallBack)) {
            this.params.cancelCallBack();
        }
        this.close();
    }
    updateHeaderAndMsgBoxText() {
        const locKey = this.getHeaderLocalizationKey();
        void this.set({
            header: Resources.getResource(locKey),
            msgBoxText: Resources.getResource(locKey + '.Message') // 'screen.confirmation.CancelOrder.Message' 'screen.confirmation.ModifyOrder.Message' 'screen.confirmation.ModifyPosition.Message' 'screen.confirmation.PlaceOrder.Message'
        });
    }
    getHeaderLocalizationKey() {
        var _a, _b;
        const type = this.params.confirmationType;
        switch (type) {
            case ConfirmationTypesEnum.OrderPlace:
                return 'screen.confirmation.PlaceOrder';
            case ConfirmationTypesEnum.OrderCancel:
                return 'screen.confirmation.CancelOrder';
            case ConfirmationTypesEnum.Modify:
                return ((_b = (_a = this.params) === null || _a === void 0 ? void 0 : _a.reqData) === null || _b === void 0 ? void 0 : _b.position) != null
                    ? 'screen.confirmation.ModifyPosition'
                    : 'screen.confirmation.ModifyOrder';
        }
    }
    updateMsgBox() {
        const msgBox = this.Controls.OldMsgBox;
        if (msgBox != null) {
            msgBox.updateWithParams('', '', this.params.type, this.params.okCallBack, this.params.cancelCallBack, this.params.showNextTimeChB, this.params.hideCancel, this.params.errorText);
            msgBox.updateWithAdditionalData(this.params.addData);
            void msgBox.set({
                top: 0,
                left: 0,
                isRelativeStyle: true,
                showBorder: false,
                showHeader: false,
                showModalMask: false,
            });
            KeyEventProcessor.OnKeyDown.Subscribe(msgBox.onGlobalKeyDown, msgBox);
            msgBox.forceCloseOnLogout = !this.params.skipOnTeardown;
            msgBox.OK_Handler = new Promise((resolve) => {
                msgBox.OkClicked = () => {
                    resolve();
                    this.close();
                };
            });
            msgBox.NO_Handler = new Promise((resolve) => {
                msgBox.CancelClicked = () => {
                    resolve();
                    this.close();
                };
            });
        }
        this.updateHeaderAndMsgBoxText();
    }
    addProductTypeEntry(entries, productType, ins) {
        if (productType != null && productType !== ProductType.General) {
            entries.push({
                key: 'panel.newOrderEntry.ProductType',
                text: InstrumentUtils.GetLocalizedProductType(ins, productType)
            });
        }
    }
    updateWithRequestData(reqData) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            const entries = [];
            let normalizedReqData;
            if (this.params.confirmationType === ConfirmationTypesEnum.OrderCancel) {
                if (!Array.isArray(reqData)) {
                    return;
                }
                normalizedReqData = this.params.reqData = OrderEditRequestData.CreateFromOrder((_a = reqData[0]) !== null && _a !== void 0 ? _a : null);
            }
            else {
                if (Array.isArray(reqData)) {
                    return;
                }
                normalizedReqData = reqData;
            }
            const ins = normalizedReqData.instrument;
            const account = normalizedReqData.account;
            const productType = normalizedReqData.TryGetProductType();
            if (ins == null || account == null) {
                return;
            }
            const modifEntity = (_b = normalizedReqData.position) !== null && _b !== void 0 ? _b : normalizedReqData.order;
            const isPos = (modifEntity === null || modifEntity === void 0 ? void 0 : modifEntity.isPosition) === true;
            const isModify = this.params.confirmationType === ConfirmationTypesEnum.Modify;
            const isModifyPosition = isModify && isPos;
            if (!isModifyPosition) {
                // #region Symbol
                entries.push({ key: 'panel.newOrderEntry.instrumentLabel', text: ins.DisplayName() });
            } // #endregion
            const isOCO = normalizedReqData.orderTypeId === OrderType.OCO;
            const ocoData = normalizedReqData.ocoCustomOrdersData;
            if (!isModifyPosition) {
                // #region Side, Order type, Quantity, Leverage
                const orderSide = (_c = ocoData === null || ocoData === void 0 ? void 0 : ocoData.firstOrderSide) !== null && _c !== void 0 ? _c : normalizedReqData.side;
                entries.push({
                    key: 'panel.newOrderEntry.Side',
                    text: Resources.getResource(`general.trading.${OrderUtils.getBuySellStr(orderSide)}`)
                });
                const orderType = isOCO ? ((_d = ocoData === null || ocoData === void 0 ? void 0 : ocoData.firstOrderType) !== null && _d !== void 0 ? _d : OrderType.Limit) : normalizedReqData.orderTypeId;
                entries.push({
                    key: 'panel.newOrderEntry.typePanel',
                    text: Resources.getResource(OrderUtils.getOrderTypeLocalizationKey(orderType))
                });
                const overnightExecution = normalizedReqData.isAllowOTHTrading;
                if (!isNullOrUndefined(overnightExecution)) {
                    entries.push({
                        key: 'property.OvernightExecution',
                        text: overnightExecution
                            ? Resources.getResource('property.True')
                            : Resources.getResource('property.False')
                    });
                }
            }
            if (!isModifyPosition) {
                const leverage = normalizedReqData.leverageValue;
                if (!isNullOrUndefined(leverage)) {
                    entries.push({ key: 'panel.newOrderEntry.Leverage', text: OrderUtils.GetFormattedLeverage(leverage) });
                }
                const amountStr = normalizedReqData.quantity == null
                    ? OrderFormatter.AmountStr(modifEntity)
                    : InstrumentUtils.formatAmountValue(normalizedReqData.quantity.value, ins, account, productType);
                if (normalizedReqData.isCashMode) {
                    entries.push({ key: 'reports.Cash', text: account.formatPrice(normalizedReqData.cash) });
                }
                else {
                    entries.push({ key: 'panel.newOrderEntry.amountLabel', text: amountStr });
                }
            } // #endregion
            // #region Limit price, Stop price, Price for cancel order
            const limitPrice = (_e = normalizedReqData.parameterDict) === null || _e === void 0 ? void 0 : _e.limitPrice;
            const stopPrice = (_f = normalizedReqData.parameterDict) === null || _f === void 0 ? void 0 : _f.stopPrice;
            let hasPrice = normalizedReqData.orderTypeId === OrderType.Market;
            if (!isNullOrUndefined(limitPrice)) {
                const key = isOCO ? 'screen.confirmation.Price' : 'panel.newOrderEntry.limitPricePanel';
                entries.push({ key, text: ins.formatPrice((_g = ocoData === null || ocoData === void 0 ? void 0 : ocoData.firstOrderLimitPriceForStopLimit) !== null && _g !== void 0 ? _g : limitPrice) });
                hasPrice = true;
            }
            if (!isNullOrUndefined(stopPrice) && !isOCO) {
                entries.push({ key: 'panel.newOrderEntry.stopPricePanel', text: ins.formatPrice(stopPrice) });
                hasPrice = true;
            }
            if (!hasPrice && !isNullOrUndefined(normalizedReqData.order)) {
                const order = normalizedReqData.order;
                const limitPriceStr = OrderFormatter.PriceStr(order);
                const stopPriceStr = OrderFormatter.StopPriceStr(order);
                if (limitPriceStr !== undefined) {
                    entries.push({
                        key: 'panel.newOrderEntry.limitPricePanel',
                        text: limitPriceStr
                    });
                }
                if (stopPriceStr !== undefined && !isNaN(order.StopLimit)) {
                    entries.push({
                        key: 'panel.newOrderEntry.stopPricePanel',
                        text: stopPriceStr
                    });
                }
            }
            // #endregion
            // #region Trailing stop
            if (((_h = normalizedReqData.parameterDict) === null || _h === void 0 ? void 0 : _h.trailingStop) != null) {
                const visualOffsetType = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(ins, BaseSettings.offsetType);
                const visualOffset = SlTpUtils.toVisualValue(normalizedReqData.parameterDict.trailingStop, ins, visualOffsetType);
                const visualOffsetStr = SlTpUtils.formatSLTPValue(visualOffset, ins, visualOffsetType);
                entries.push({ key: 'Tr. stop', text: visualOffsetStr + ' (offset)' });
            } // #endregion
            // #region OCO
            if (isOCO) {
                const secondOrderType = (_j = ocoData === null || ocoData === void 0 ? void 0 : ocoData.secondOrderType) !== null && _j !== void 0 ? _j : OrderType.Stop;
                entries.push({
                    key: 'screen.confirmation.Type',
                    text: Resources.getResource(OrderUtils.getOrderTypeLocalizationKey(secondOrderType))
                });
                const secondOrderSide = (_k = ocoData === null || ocoData === void 0 ? void 0 : ocoData.secondOrderSide) !== null && _k !== void 0 ? _k : normalizedReqData.side;
                entries.push({
                    key: 'screen.confirmation.Operation',
                    text: Resources.getResource(`general.trading.${OrderUtils.getBuySellStr(secondOrderSide)}`)
                });
                const stopPrice = (_l = ocoData === null || ocoData === void 0 ? void 0 : ocoData.secondOrderLimitPriceForStopLimit) !== null && _l !== void 0 ? _l : (_m = normalizedReqData.parameterDict) === null || _m === void 0 ? void 0 : _m.stopPrice;
                if (stopPrice != null) {
                    entries.push({ key: 'screen.confirmation.Price', text: ins.formatPrice(stopPrice) });
                }
            } // #endregion
            if (!isModifyPosition) {
                // #region Validity(TIF)
                let tifText;
                const tif = (_o = ocoData === null || ocoData === void 0 ? void 0 : ocoData.firstOrderTif) !== null && _o !== void 0 ? _o : normalizedReqData.tif;
                if (!isNullOrUndefined(tif)) {
                    tifText = OrderFormatter.GetFormattedValidityStr(tif.type, tif.expirationTime);
                }
                else if (modifEntity != null) {
                    // hsa: I'm not sure about this part
                    tifText = OrderFormatter.ValidityStr(modifEntity);
                }
                if (!isNullOrUndefined(tifText)) {
                    entries.push({ key: 'panel.newOrderEntry.tifPanel', text: tifText });
                }
            } // #endregion
            // #region SL/TP
            const iSlTPEdit = (_p = normalizedReqData.parameterDict) === null || _p === void 0 ? void 0 : _p.sltp;
            const sltpHolder = iSlTPEdit === null || iSlTPEdit === void 0 ? void 0 : iSlTPEdit.sltpHolder;
            if (!isNullOrUndefined(sltpHolder)) {
                // entries.push({ key: '', text: ins.formatPrice(limitPrice) });
                const triggersShV = sltpHolder.SLTPTriggerShortValue;
                let slLabel = null;
                let sllLabel = null;
                let tpLabel = null;
                let slTrigger = null;
                let tpTrigger = null;
                let isAllowOTHTradingSLLabel = null;
                let isAllowOTHTradingTPLabel = null;
                if (!sltpHolder.isSlEmpty()) {
                    const slKey = sltpHolder.isSlAbsolute()
                        ? 'screen.confirmation.SLprice'
                        : sltpHolder.isTrailingStop()
                            ? 'Tr. SL offset'
                            : 'SL offset';
                    const slVisualOffsetType = SlTpUtils.getSLVisualOffsetType(ins, sltpHolder, BaseSettings.offsetType);
                    const slVisualOffset = SlTpUtils.toVisualValue(sltpHolder.StopLossPriceValue, ins, slVisualOffsetType);
                    const slVisualOffsetStr = SlTpUtils.formatSLTPValue(slVisualOffset, ins, slVisualOffsetType);
                    let slVisualOffsetDescr = Resources.getResource(SlTpUtils.getLocalizationForOffsetMode(slVisualOffsetType));
                    if (isValidString(slVisualOffsetDescr)) {
                        slVisualOffsetDescr = ` ${slVisualOffsetDescr}`;
                    }
                    slLabel = { key: slKey, text: `${slVisualOffsetStr} ${slVisualOffsetDescr}` };
                    if (!sltpHolder.isSllEmpty()) {
                        const sllKey = sltpHolder.isSlAbsolute() ? 'SL limit price' : 'SL limit offset';
                        const sllVisualOffset = SlTpUtils.toVisualValue(sltpHolder.StopLossLimitPriceValue, ins, slVisualOffsetType);
                        const sllVisualOffsetStr = SlTpUtils.formatSLTPValue(sllVisualOffset, ins, slVisualOffsetType);
                        sllLabel = { key: sllKey, text: `${sllVisualOffsetStr} ${slVisualOffsetDescr}` };
                    }
                    slTrigger = { key: 'SL trigger', text: SLTPTrigger.GetTextValue(triggersShV, normalizedReqData.side, true) };
                    const isAllowOTHTradingSL = iSlTPEdit.isAllowOTHTradingSL;
                    if (!isNullOrUndefined(isAllowOTHTradingSL)) {
                        isAllowOTHTradingSLLabel = {
                            key: 'property.OvernightExecution.SL',
                            text: isAllowOTHTradingSL
                                ? Resources.getResource('property.True')
                                : Resources.getResource('property.False')
                        };
                    }
                }
                if (!sltpHolder.isTpEmpty()) {
                    const tpKey = sltpHolder.isTpAbsolute() ? 'screen.confirmation.TPprice' : 'TP offset';
                    const tpVisualOffsetType = SlTpUtils.getTPVisualOffsetType(ins, sltpHolder, BaseSettings.offsetType);
                    const tpVisualOffset = SlTpUtils.toVisualValue(sltpHolder.TakeProfitPriceValue, ins, tpVisualOffsetType);
                    const tpVisualOffsetStr = SlTpUtils.formatSLTPValue(tpVisualOffset, ins, tpVisualOffsetType);
                    let tpVisualOffsetDescr = Resources.getResource(SlTpUtils.getLocalizationForOffsetMode(tpVisualOffsetType));
                    if (isValidString(tpVisualOffsetDescr)) {
                        tpVisualOffsetDescr = ` ${tpVisualOffsetDescr}`;
                    }
                    tpLabel = { key: tpKey, text: `${tpVisualOffsetStr} ${tpVisualOffsetDescr}` };
                    tpTrigger = { key: 'TP trigger', text: SLTPTrigger.GetTextValue(triggersShV, normalizedReqData.side, false) };
                    const isAllowOTHTradingTP = iSlTPEdit.isAllowOTHTradingTP;
                    if (!isNullOrUndefined(isAllowOTHTradingTP)) {
                        isAllowOTHTradingTPLabel = {
                            key: 'property.OvernightExecution.TP',
                            text: isAllowOTHTradingTP
                                ? Resources.getResource('property.True')
                                : Resources.getResource('property.False')
                        };
                    }
                }
                if (slLabel != null) {
                    entries.push(slLabel);
                }
                if ((slTrigger === null || slTrigger === void 0 ? void 0 : slTrigger.text) != null) {
                    entries.push(slTrigger);
                }
                if (sllLabel != null) {
                    entries.push(sllLabel);
                }
                if (isAllowOTHTradingSLLabel != null) {
                    entries.push(isAllowOTHTradingSLLabel);
                }
                if (tpLabel != null) {
                    entries.push(tpLabel);
                }
                if ((tpTrigger === null || tpTrigger === void 0 ? void 0 : tpTrigger.text) != null) {
                    entries.push(tpTrigger);
                }
                if (isAllowOTHTradingTPLabel != null) {
                    entries.push(isAllowOTHTradingTPLabel);
                }
            } // #endregion
            if (!isModifyPosition) {
                // #region Symbol type, Route
                entries.push({ key: 'reports.instrumenttype', text: ins.getTypeString() });
                if (!ins.isHideRouteMode) {
                    entries.push({
                        key: 'panel.orders.Route',
                        text: (_q = ConfirmationScreen.DataCache.Routes.getRouteById(ins.Route)) === null || _q === void 0 ? void 0 : _q.Name
                    });
                }
            } // #endregion
            // #region Account
            entries.push({ key: 'AdditionalProperty.Account', text: account.toString() });
            // #endregion
            // #region OrderID/PositionID
            if (modifEntity != null) {
                entries.push({
                    key: isPos ? 'screen.confirmation.positionID' : 'screen.confirmation.orderID',
                    text: modifEntity.OrderNumber
                });
            } // #endregion
            // #region Product type
            this.addProductTypeEntry(entries, productType, ins);
            // #endregion
            if (!isNullOrUndefined(normalizedReqData.placedFrom) && !isModify) {
                const [totalFee, estAvailableCash] = [normalizedReqData.totalFee, normalizedReqData.estAvailableCash];
                const assetBalance = account.assetBalanceDefault;
                // #region Estimated Fee
                const feeText = MarginInfoUtils.formatCalculableNumeric(totalFee, (v) => assetBalance.formatPrice(Math.abs(v)));
                if (feeText !== null) {
                    entries.push({
                        key: 'screen.confirmation.EstimatedFee',
                        text: feeText
                    });
                }
                // #endregion
                // #region Est. available cash (ex. After Trade Cash)
                const cashText = MarginInfoUtils.formatCalculableNumeric(estAvailableCash, (v) => assetBalance.formatPrice(v));
                if (cashText !== null) {
                    entries.push({
                        key: 'screen.confirmation.AfterTradeCash',
                        text: cashText
                    });
                }
                // #endregion
            }
            entries.forEach((e) => {
                e.caption = `${Resources.getResource(e.key)}:`;
            });
            void this.set({ entries });
        });
    }
    static MessageBoxHandlerInitialize() {
        confirmationScreenHandler.Show = ConfirmationScreen.Show;
        confirmationScreenHandler.msgType = MessageBoxType;
    }
    static Show(confirmationType, reqData, type, okCallBack, cancelCallBack, showNextTimeChB, hideCancel, errorText, addData, skipOnTeardown = false, asyncConfirmationParams) {
        const confirmScreen = new ConfirmationScreen();
        // Save parameters for later use in oncomplete
        confirmScreen.params = {
            type,
            okCallBack,
            cancelCallBack,
            showNextTimeChB,
            hideCancel,
            errorText,
            addData,
            skipOnTeardown,
            reqData,
            confirmationType,
            asyncConfirmationParams
        };
        MainWindowManager.AddControl(confirmScreen);
        return confirmScreen;
    }
}
TerceraWindowBase.extendWith(ConfirmationScreen, {
    data: function () {
        return {
            showFooter: false,
            msgBoxText: '',
            entries: []
        };
    },
    partials: { bodyPartial: ConfirmationScreenTemplate }
});
